import type { UploadQueueEntry } from '@/hooks/useUploadQueue';
import { Check, FileUp, Loader2, XCircle } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadQueuePanelProps {
  uploads: UploadQueueEntry[];
  onCancel?: (id: string) => void;
}

export const UploadQueuePanel: FC<UploadQueuePanelProps> = ({ uploads }) => {
  const { t } = useTranslation();

  if (uploads.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-20 right-4 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg"
      aria-label={t('projects.uploadQueueLabel', { count: uploads.length })}
    >
      <div className="border-b border-gray-100 px-3 py-2">
        <h3 className="text-sm font-semibold text-gray-700">
          {t('projects.uploadingDocuments')} ({uploads.length})
        </h3>
      </div>
      <ul className="max-h-64 overflow-y-auto p-2">
        {uploads.map((entry) => (
          <li key={entry.id} className="flex items-start gap-2 rounded-md border border-gray-100 p-2 text-sm last:mb-0">
            <div className="mt-0.5 shrink-0">
              {entry.status === 'uploading' || entry.status === 'processing' ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
              ) : entry.status === 'done' ? (
                <Check className="h-4 w-4 text-green-600" aria-hidden />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <FileUp className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                <span className="truncate font-medium text-gray-800" title={entry.title}>
                  {entry.title}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-1.5 gap-y-0.5 text-xs text-gray-500">
                {entry.fileNames.map((name) => (
                  <span key={name} className="truncate max-w-[180px]" title={name}>
                    {name}
                  </span>
                ))}
              </div>
              {(entry.status === 'uploading' || entry.status === 'processing') && (
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div className="indeterminate-progress-bar h-full rounded-full bg-primary" />
                </div>
              )}
              {entry.status === 'error' && entry.error && (
                <p className="mt-1 text-xs text-red-600" title={entry.error}>
                  {entry.error}
                </p>
              )}
            </div>
            {/* TODO: Cancel upload button — hidden until backend supports preemptive task cancellation.
                 Backend cooperative cancel (Redis flag) does not interrupt _upload_3d_model mid-execution.
                 See future-extensions.md for the planned implementation. */}
            {/* {onCancel && entry.cancel && (entry.status === 'uploading' || entry.status === 'processing') && (
              <button
                type="button"
                onClick={() => onCancel(entry.id)}
                className="mt-0.5 shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title={t('projects.cancelUpload')}
                aria-label={t('projects.cancelUpload')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )} */}
          </li>
        ))}
      </ul>
    </div>
  );
};
